'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { runWhenIdle } from '@/src/lib/utils/runWhenIdle';

// The idle deadline also caps how long a keyboard-only user waits for a focusable map.
export const MAP_ACTIVATION_IDLE_TIMEOUT_MS = 2500;
// Deadline cap for an uncovered panel. The idle callback normally fires well
// before it; a panel with nothing drawn over it just can't wait out the longer one.
const MAP_ACTIVATION_UNCOVERED_TIMEOUT_MS = 150;
// Matches the overlay's transitionDuration in PersistentSearchShell.
const MAP_PREVIEW_FADE_OUT_MS = 400;
// Caps how long the opaque overlay can swallow pointer events when Maps JS is
// blocked and tilesloaded never fires.
const MAP_PREVIEW_FAILSAFE_DISMISS_MS = 4000;

type DeferredMapIslandOptions = {
  hasPreview: boolean;
  shouldRenderMap: boolean;
  mobileMapActive: boolean;
  pathname: string;
};

/**
 * Defers mounting the Google Maps island while a static map preview covers the
 * panel: the island mounts on first panel interaction or post-hydration idle,
 * and the preview fades once the map's tiles have painted.
 */
export function useDeferredMapIsland({
  hasPreview,
  shouldRenderMap,
  mobileMapActive,
  pathname,
}: DeferredMapIslandOptions) {
  const [mapActivated, setMapActivated] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isPreviewDismissed, setIsPreviewDismissed] = useState(false);
  const initialPathnameRef = useRef(pathname);

  const activateMap = useCallback(() => setMapActivated(true), []);
  const handleMapReady = useCallback(() => setIsMapReady(true), []);
  // A failed preview image leaves nothing worth covering the map with.
  const handlePreviewError = useCallback(() => {
    setIsPreviewDismissed(true);
    setMapActivated(true);
  }, []);

  const showPreview = hasPreview && !isPreviewDismissed;
  // Mobile map opens are gated too: constructing Maps JS in the tap's own commit
  // cost ~190ms of INP (HP-8727).
  const isGateActive = !mapActivated && (showPreview || mobileMapActive);
  const showIsland = shouldRenderMap && !isGateActive;
  // The preview is lg-only, so it never covers a panel opened on mobile.
  const isPanelCovered = showPreview && !mobileMapActive;

  // A soft navigation makes the snapshotted preview stale.
  useEffect(() => {
    if (pathname !== initialPathnameRef.current) setIsPreviewDismissed(true);
  }, [pathname]);

  useEffect(() => {
    if (!shouldRenderMap || !isGateActive) return;
    return runWhenIdle(activateMap, {
      timeoutMs: isPanelCovered ? MAP_ACTIVATION_IDLE_TIMEOUT_MS : MAP_ACTIVATION_UNCOVERED_TIMEOUT_MS,
    });
  }, [activateMap, isGateActive, isPanelCovered, shouldRenderMap]);

  // The island unmounts with the mobile panel, so re-arm for the next open;
  // desktop holds `shouldRenderMap` true and never re-arms.
  useEffect(() => {
    if (!shouldRenderMap) setMapActivated(false);
  }, [shouldRenderMap]);

  // Ready tiles unmount the overlay once its fade completes; an island whose
  // tiles never paint gets the failsafe instead.
  useEffect(() => {
    if (!showPreview || (!isMapReady && !showIsland)) return;
    const handle = window.setTimeout(
      () => setIsPreviewDismissed(true),
      isMapReady ? MAP_PREVIEW_FADE_OUT_MS : MAP_PREVIEW_FAILSAFE_DISMISS_MS,
    );
    return () => window.clearTimeout(handle);
  }, [isMapReady, showIsland, showPreview]);

  return {
    showIsland,
    showPreview,
    isMapReady,
    onActivate: activateMap,
    onMapReady: handleMapReady,
    onPreviewError: handlePreviewError,
  };
}
